import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { Gavel, Plus, Pencil, Trash2, ShieldCheck, Loader2, Check, Download } from "lucide-react";
import { EmailImport, ContactEmailInline } from "@/components/admin/EmailImport";

const EMPTY = {
  name: "", firm: "", practice_areas: [], counties: [], city: "",
  phone: "", email: "", website: "", bio: "", is_resource: false, is_active: true,
};

export default function AdminAttorneys() {
  const [attorneys, setAttorneys] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [specs, setSpecs] = useState([]);
  const [importSpec, setImportSpec] = useState("");
  const [importing, setImporting] = useState(false);
  const [bulk, setBulk] = useState(null);
  const [autoPublish, setAutoPublish] = useState(false);
  useEffect(() => { api.get("/admin/directory-settings").then(({ data }) => setAutoPublish(!!data.claim_auto_publish)).catch(() => {}); }, []);
  const toggleAutoPublish = async (v) => {
    setAutoPublish(v);
    try { await api.post("/admin/directory-settings", { claim_auto_publish: v }); toast.success(v ? "Verified claims will auto-publish" : "Claims now require admin approval"); }
    catch { toast.error("Could not save setting."); setAutoPublish(!v); }
  };

  const pollBulk = async () => {
    try {
      const { data } = await api.get("/admin/attorneys/import-status");
      setBulk(data);
      if (data.running) { setTimeout(pollBulk, 2500); }
      else { toast.success(`Bulk import complete — ${data.imported} specialists imported.`); load(); setTimeout(() => setBulk(null), 4000); }
    } catch { /* ignore */ }
  };

  const runBulk = async () => {
    try {
      await api.post("/admin/attorneys/import-ncbar-all");
      toast.success("Importing all specialties in the background…");
      setBulk({ running: true, done: 0, total: 19, imported: 0, current: "" });
      setTimeout(pollBulk, 2000);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start bulk import."); }
  };

  const runImport = async () => {
    if (!importSpec) { toast.error("Choose a specialty to import."); return; }
    setImporting(true);
    try {
      const { data } = await api.post("/admin/attorneys/import-ncbar", { specialty: importSpec });
      toast.success(`Imported ${data.imported} ${importSpec} specialists (→ ${data.mapped_area}).`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed. Try again.");
    } finally { setImporting(false); }
  };

  const load = () => { setLoading(true); api.get("/admin/attorneys").then(({ data }) => setAttorneys(data.attorneys)).finally(() => setLoading(false)); };
  useEffect(() => { load(); api.get("/directory/practice-areas").then(({ data }) => setAreas(data.practice_areas)).catch(() => {}); api.get("/admin/attorneys/ncbar-specialties").then(({ data }) => setSpecs(data.specialties)).catch(() => {}); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (a) => { setEditing(a.id); setForm({ ...EMPTY, ...a }); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleArea = (a) => setForm((f) => ({ ...f, practice_areas: f.practice_areas.includes(a) ? f.practice_areas.filter((x) => x !== a) : [...f.practice_areas, a] }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required."); return; }
    setSaving(true);
    const payload = { ...form, counties: typeof form.counties === "string" ? form.counties.split(",").map((s) => s.trim()).filter(Boolean) : form.counties };
    try {
      if (editing) await api.put(`/admin/attorneys/${editing}`, payload);
      else await api.post("/admin/attorneys", payload);
      toast.success(editing ? "Attorney updated" : "Attorney added");
      setOpen(false); load();
    } catch { toast.error("Could not save attorney."); }
    finally { setSaving(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Remove ${a.name} from the directory?`)) return;
    try { await api.delete(`/admin/attorneys/${a.id}`); toast.success("Removed"); load(); }
    catch { toast.error("Could not remove."); }
  };

  const approve = async (a) => {
    try { await api.post(`/admin/attorneys/${a.id}/approve`); toast.success(`${a.name} approved & published`); load(); }
    catch { toast.error("Could not approve."); }
  };

  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/attorneys/export.csv", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `nc-lawyer-directory-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Could not export the directory"); }
    finally { setExporting(false); }
  };

  const approveClaim = async (a) => {
    try { await api.post(`/admin/attorneys/${a.id}/approve-claim`); toast.success(`Claim approved — ${a.name}'s details updated`); load(); }
    catch { toast.error("Could not approve claim."); }
  };

  return (
    <div data-testid="admin-attorneys">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center w-10 h-10 bg-primary text-primary-foreground"><Gavel size={20} /></span>
          <div>
            <h1 className="font-serif text-3xl tracking-tight">Attorney Directory</h1>
            <p className="text-sm text-muted-foreground">Manage the in-app "Find an Attorney" listings.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCsv} disabled={exporting} data-testid="export-attorneys-btn" className="rounded-sm gap-2">
            {exporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} Export spreadsheet
          </Button>
          <Button onClick={openNew} data-testid="add-attorney-btn" className="rounded-sm gap-2"><Plus size={16} /> Add attorney</Button>
        </div>
      </div>

      <div className="border-2 border-black/10 bg-secondary/40 p-4 mb-6 flex items-center justify-between gap-4" data-testid="auto-publish-setting">
        <div>
          <p className="text-sm font-semibold">Auto-publish verified claims</p>
          <p className="text-xs text-muted-foreground max-w-md">When on, a listing goes live instantly once the claimant confirms their email — no admin approval needed.</p>
        </div>
        <Switch checked={autoPublish} onCheckedChange={toggleAutoPublish} data-testid="auto-publish-toggle" />
      </div>

      <div className="border-2 border-black/10 bg-secondary/40 p-4 mb-6 flex flex-col sm:flex-row sm:items-end gap-3" data-testid="ncbar-import">
        <div className="flex-1">
          <p className="text-sm font-semibold">Import board-certified specialists from the NC State Bar</p>
          <p className="text-xs text-muted-foreground">Pulls the public list of board-certified specialists for a specialty and adds them to the directory.</p>
        </div>
        <select value={importSpec} onChange={(e) => setImportSpec(e.target.value)} data-testid="ncbar-specialty-select"
          className="h-10 rounded-sm border-2 border-black/15 bg-background px-3 text-sm sm:w-64">
          <option value="">Choose a specialty…</option>
          {specs.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <Button onClick={runImport} disabled={importing || bulk?.running} data-testid="ncbar-import-btn" className="rounded-sm gap-2 shrink-0">
          {importing ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Import
        </Button>
        <Button onClick={runBulk} disabled={bulk?.running} variant="outline" data-testid="ncbar-import-all-btn" className="rounded-sm gap-2 shrink-0">
          {bulk?.running ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Import ALL
        </Button>
      </div>
      {bulk && (
        <div className="-mt-3 mb-6 text-sm text-muted-foreground" data-testid="bulk-progress">
          {bulk.running
            ? `Importing all specialties… ${bulk.done}/${bulk.total} done · ${bulk.imported} added${bulk.current ? ` · ${bulk.current}` : ""}`
            : `Done — ${bulk.imported} specialists imported.`}
        </div>
      )}

      {loading ? (
        <p className="text-muted-foreground py-8 text-center animate-pulse">Loading…</p>
      ) : (
        <>
        <EmailImport onDone={() => api.get("/admin/attorneys").then(({ data }) => setAttorneys(data.attorneys))} />
        <div className="border-2 border-black/10 divide-y divide-border" data-testid="attorney-admin-list">
          {attorneys.map((a) => (
            <div key={a.id} className="flex items-start justify-between gap-4 p-4" data-testid={`attorney-row-${a.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold">{a.name}</span>
                  {a.status === "pending" && <Badge className="rounded-sm bg-amber-500 hover:bg-amber-500 text-white gap-1" data-testid={`pending-badge-${a.id}`}>Pending review</Badge>}
                  {a.pending_claim && <Badge className="rounded-sm bg-blue-600 hover:bg-blue-600 text-white gap-1" data-testid={`claim-badge-${a.id}`}>Claim pending</Badge>}
                  {a.is_resource && <Badge className="rounded-sm bg-accent text-accent-foreground gap-1"><ShieldCheck size={11} /> Resource</Badge>}
                  {a.is_active === false && a.status !== "pending" && <Badge variant="outline" className="rounded-sm text-muted-foreground">Hidden</Badge>}
                </div>
                {a.firm && <p className="text-sm text-muted-foreground">{a.firm}{a.city ? ` · ${a.city}` : ""}{a.email ? ` · ${a.email}` : ""}</p>}
                {a.practice_areas?.length > 0 && <p className="text-xs text-muted-foreground mt-1">{a.practice_areas.join(", ")}</p>}
                {!a.is_resource && <ContactEmailInline a={a} onSaved={(e) => setAttorneys((list) => list.map((x) => (x.id === a.id ? { ...x, contact_email: e } : x)))} />}
              </div>
              <div className="flex gap-2 shrink-0">
                {a.status === "pending" && (
                  <Button size="sm" onClick={() => approve(a)} data-testid={`approve-attorney-${a.id}`} className="rounded-sm gap-1 bg-emerald-600 hover:bg-emerald-700"><Check size={13} /> Approve</Button>
                )}
                {a.pending_claim && (
                  <Button size="sm" onClick={() => approveClaim(a)} data-testid={`approve-claim-${a.id}`} className="rounded-sm gap-1 bg-blue-600 hover:bg-blue-700"><Check size={13} /> Approve claim</Button>
                )}
                <Button size="sm" variant="outline" onClick={() => openEdit(a)} data-testid={`edit-attorney-${a.id}`} className="rounded-sm gap-1"><Pencil size={13} /> Edit</Button>
                <Button size="sm" variant="outline" onClick={() => remove(a)} data-testid={`delete-attorney-${a.id}`} className="rounded-sm gap-1 text-[#E63946] hover:bg-red-50"><Trash2 size={13} /></Button>
              </div>
            </div>
          ))}
          {attorneys.length === 0 && <p className="p-8 text-center text-muted-foreground text-sm">No attorneys yet. Click "Add attorney".</p>}
        </div>
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg max-h-[85vh] overflow-y-auto" data-testid="attorney-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editing ? "Edit attorney" : "Add attorney"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Name *</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="attorney-name-input" className="rounded-sm mt-1" /></div>
              <div><Label className="text-xs">Firm</Label><Input value={form.firm} onChange={(e) => set("firm", e.target.value)} className="rounded-sm mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">City</Label><Input value={form.city} onChange={(e) => set("city", e.target.value)} className="rounded-sm mt-1" /></div>
              <div><Label className="text-xs">Counties (comma-separated)</Label><Input value={Array.isArray(form.counties) ? form.counties.join(", ") : form.counties} onChange={(e) => set("counties", e.target.value)} className="rounded-sm mt-1" placeholder="Wake, Durham" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="rounded-sm mt-1" /></div>
              <div><Label className="text-xs">Email</Label><Input value={form.email} onChange={(e) => set("email", e.target.value)} className="rounded-sm mt-1" /></div>
            </div>
            <div><Label className="text-xs">Website</Label><Input value={form.website} onChange={(e) => set("website", e.target.value)} className="rounded-sm mt-1" placeholder="https://" /></div>
            <div><Label className="text-xs">Short bio</Label><Textarea value={form.bio} onChange={(e) => set("bio", e.target.value)} className="rounded-sm mt-1" rows={3} /></div>
            <div>
              <Label className="text-xs">Practice areas</Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {areas.map((a) => (
                  <button key={a} type="button" onClick={() => toggleArea(a)}
                    className={`text-xs px-2.5 py-1 border-2 transition-colors ${form.practice_areas.includes(a) ? "border-accent bg-accent/10" : "border-black/15 hover:border-black/40"}`}>
                    {a}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-6 pt-1">
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.is_resource} onCheckedChange={(v) => set("is_resource", v)} /> Statewide resource</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.is_active} onCheckedChange={(v) => set("is_active", v)} /> Visible</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-attorney-btn" className="rounded-sm gap-2">{saving && <Loader2 size={15} className="animate-spin" />} Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
